import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  fetchLiveMomentTicker,
  searchPublicMoments,
  type MomentSearchMomentResult,
  type MomentSearchPublisherResult,
  type MomentSearchResponse,
  type MomentSearchSessionResult,
  type MomentTickerResponse,
  type MomentTickerResult,
} from '../services/momentSearch.service';
import './MomentSearchPage.css';

type SearchMode = 'search' | 'ticker';
type ResultFilter = 'all' | 'liveMoments' | 'live' | 'upcoming' | 'moments' | 'past' | 'publishers';
type LoadMoreGroup = Exclude<ResultFilter, 'all'>;

interface LoadMoreState {
  loading: boolean;
  error: string;
}

interface ResultFilterOption {
  id: ResultFilter;
  label: string;
  count: number;
}

const RESULT_GROUPS: LoadMoreGroup[] = ['liveMoments', 'live', 'upcoming', 'moments', 'past', 'publishers'];
const SEARCH_PAGE_GROUP_LIMIT = 10;
const MAX_SESSION_MOMENT_TYPE_BADGES = 4;
const LIVE_TICKER_LIMIT = 20;
const LIVE_TICKER_FALLBACK_POLL_INTERVAL_MS = 15_000;

const createEmptyLoadMoreState = (): Record<LoadMoreGroup, LoadMoreState> => ({
  liveMoments: { loading: false, error: '' },
  live: { loading: false, error: '' },
  upcoming: { loading: false, error: '' },
  moments: { loading: false, error: '' },
  past: { loading: false, error: '' },
  publishers: { loading: false, error: '' },
});

const getDefaultPagination = () => ({ hasMore: false, nextCursor: null as string | null });

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  return `${datePart}, ${timePart}`;
};

const formatRelativeTime = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return '';
  }

  const diffMs = Date.now() - timestamp;
  if (diffMs < 0) {
    return 'just now';
  }

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) {
    return `${Math.max(1, seconds)}s ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  return formatDateTime(value);
};

const getCategoryIcon = (category: string): string => {
  const normalized = category.trim().toLowerCase();
  if (normalized.includes('football') || normalized.includes('soccer')) return '⚽';
  if (normalized.includes('basketball')) return '🏀';
  if (normalized.includes('tennis')) return '🎾';
  if (normalized.includes('hockey')) return '🏒';
  if (normalized.includes('baseball')) return '⚾';
  if (normalized.includes('golf')) return '⛳';
  if (normalized.includes('motorsport') || normalized.includes('racing') || normalized.includes('formula')) return '🏎️';
  if (normalized.includes('esport') || normalized.includes('gaming')) return '🎮';
  if (normalized.includes('music') || normalized.includes('concert')) return '🎵';
  if (normalized.includes('news')) return '📰';
  if (
    normalized.includes('people')
    || normalized.includes('person')
    || normalized.includes('creator')
    || normalized.includes('publisher')
    || normalized.includes('streamer')
    || normalized.includes('host')
  ) {
    return '👥';
  }
  return '🎬';
};

const isPeopleCategory = (category: string): boolean => {
  const normalized = category.trim().toLowerCase();
  return normalized.includes('people')
    || normalized.includes('person')
    || normalized.includes('creator')
    || normalized.includes('publisher')
    || normalized.includes('streamer')
    || normalized.includes('host');
};

const sortPeopleSessionsLast = (results: MomentSearchSessionResult[]): MomentSearchSessionResult[] =>
  [...results].sort((left, right) => {
    const leftIsPeople = isPeopleCategory(left.categoryLabel || left.category);
    const rightIsPeople = isPeopleCategory(right.categoryLabel || right.category);
    if (leftIsPeople === rightIsPeople) {
      return 0;
    }
    return leftIsPeople ? 1 : -1;
  });

const isDefaultMomentDescription = (description: string): boolean =>
  description.trim().toLowerCase() === 'spoiler-safe moment available on hypedirector.';

const isWithinLastMinute = (value: string | null): boolean => {
  if (!value) {
    return false;
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return false;
  }

  const ageMs = Date.now() - timestamp;
  return ageMs >= 0 && ageMs <= 60_000;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const SearchCardVisual: React.FC<{
  imageUrl?: string | null;
  icon: string;
  label: string;
  showLiveIndicator?: boolean;
}> = ({ imageUrl, icon, label, showLiveIndicator = false }) => (
  <div className="moment-search-card-visual" aria-hidden="true">
    {showLiveIndicator && <span className="moment-search-card-live-dot" />}
    {imageUrl
      ? <img className="moment-search-card-visual-image" src={imageUrl} alt="" loading="lazy" />
      : <span className="moment-search-card-visual-icon">{icon}</span>}
    {label && <span className="moment-search-card-visual-label">{label}</span>}
  </div>
);

const countResults = (response: MomentSearchResponse | null): number => {
  if (!response) {
    return 0;
  }
  return response.groups.liveMoments.length
    + response.groups.live.length
    + response.groups.upcoming.length
    + response.groups.moments.length
    + response.groups.past.length
    + response.groups.publishers.length;
};

const getResultFilterCount = (response: MomentSearchResponse | null, filter: ResultFilter): number => {
  if (!response) {
    return 0;
  }
  if (filter === 'all') {
    return countResults(response);
  }
  return response.groups[filter].length;
};

const getGroupPagination = (response: MomentSearchResponse | null, group: LoadMoreGroup) =>
  response?.meta.pagination?.[group] ?? getDefaultPagination();

const mergeById = <T extends { id: string }>(existing: T[], incoming: T[]): T[] => {
  const merged = new Map<string, T>();
  for (const item of existing) {
    merged.set(item.id, item);
  }
  for (const item of incoming) {
    merged.set(item.id, item);
  }
  return Array.from(merged.values());
};

const mergePagination = (
  previous: MomentSearchResponse,
  next: MomentSearchResponse,
  group: LoadMoreGroup,
) => {
  const pagination = {
    ...RESULT_GROUPS.reduce(
      (acc, resultGroup) => ({ ...acc, [resultGroup]: getGroupPagination(previous, resultGroup) }),
      {} as NonNullable<MomentSearchResponse['meta']['pagination']>,
    ),
  };
  pagination[group] = getGroupPagination(next, group);
  return pagination;
};

const appendSearchGroup = (
  previous: MomentSearchResponse,
  next: MomentSearchResponse,
  group: LoadMoreGroup,
): MomentSearchResponse => {
  const pagination = mergePagination(previous, next, group);
  const meta = {
    ...previous.meta,
    defaultResults: next.meta.defaultResults,
    minimumQueryLength: next.meta.minimumQueryLength,
    searched: next.meta.searched,
    pagination,
  };

  switch (group) {
    case 'liveMoments':
      return {
        ...previous,
        generatedAtIso: next.generatedAtIso,
        groups: { ...previous.groups, liveMoments: mergeById(previous.groups.liveMoments, next.groups.liveMoments) },
        meta,
      };
    case 'live':
      return {
        ...previous,
        generatedAtIso: next.generatedAtIso,
        groups: { ...previous.groups, live: mergeById(previous.groups.live, next.groups.live) },
        meta,
      };
    case 'upcoming':
      return {
        ...previous,
        generatedAtIso: next.generatedAtIso,
        groups: { ...previous.groups, upcoming: mergeById(previous.groups.upcoming, next.groups.upcoming) },
        meta,
      };
    case 'past':
      return {
        ...previous,
        generatedAtIso: next.generatedAtIso,
        groups: { ...previous.groups, past: mergeById(previous.groups.past, next.groups.past) },
        meta,
      };
    case 'moments':
      return {
        ...previous,
        generatedAtIso: next.generatedAtIso,
        groups: { ...previous.groups, moments: mergeById(previous.groups.moments, next.groups.moments) },
        meta,
      };
    case 'publishers':
      return {
        ...previous,
        generatedAtIso: next.generatedAtIso,
        groups: { ...previous.groups, publishers: mergeById(previous.groups.publishers, next.groups.publishers) },
        meta,
      };
    default:
      return previous;
  }
};

const formatCompactCount = (count: number): string => (
  count >= 1000
    ? new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(count)
    : new Intl.NumberFormat(undefined).format(count)
);

const getSessionPlayerUrl = (result: MomentSearchSessionResult): string => {
  try {
    const url = new URL(result.fanUrl);
    url.pathname = `/player/${encodeURIComponent(result.id)}`;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return result.fanUrl.replace(`/session/${encodeURIComponent(result.id)}`, `/player/${encodeURIComponent(result.id)}`);
  }
};

const getSessionActionUrl = (result: MomentSearchSessionResult): string => {
  if (result.group !== 'past') {
    return result.fanUrl;
  }

  try {
    const url = new URL(result.fanUrl);
    url.pathname = `/ended-session/${encodeURIComponent(result.id)}`;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return result.fanUrl.replace(`/session/${encodeURIComponent(result.id)}`, `/ended-session/${encodeURIComponent(result.id)}`);
  }
};

const getMomentPlayerUrl = (result: Pick<MomentSearchMomentResult, 'fanUrl' | 'sessionId' | 'id'>): string => {
  try {
    const url = new URL(result.fanUrl);
    url.pathname = `/player/${encodeURIComponent(result.sessionId)}/${encodeURIComponent(result.id)}`;
    url.search = '?cutToMoment=true';
    url.hash = '';
    return url.toString();
  } catch {
    return result.fanUrl.replace(`/m/${encodeURIComponent(result.id)}`, `/player/${encodeURIComponent(result.sessionId)}/${encodeURIComponent(result.id)}?cutToMoment=true`);
  }
};

const SessionCard: React.FC<{ result: MomentSearchSessionResult }> = ({ result }) => {
  const time = formatDateTime(result.startTimeIso);
  const actionLabel = result.group === 'upcoming' ? 'Open in app' : 'View Session';
  const actionUrl = getSessionActionUrl(result);
  const category = result.categoryLabel || result.category || 'Session';
  const momentTypeLabels = Array.isArray(result.momentTypeLabels) ? result.momentTypeLabels : [];
  const visibleMomentTypeLabels = momentTypeLabels.slice(0, MAX_SESSION_MOMENT_TYPE_BADGES);
  const hiddenMomentTypeCount = Math.max(0, momentTypeLabels.length - visibleMomentTypeLabels.length);
  const openSession = () => {
    window.location.assign(actionUrl);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openSession();
    }
  };

  return (
    <article
      className={`moment-search-card moment-search-session-card${result.group === 'live' ? ' moment-search-session-card-live' : ''}`}
      role="link"
      tabIndex={0}
      onClick={openSession}
      onKeyDown={handleKeyDown}
      aria-label={`${actionLabel}: ${result.title}`}
    >
      <SearchCardVisual icon={result.categoryIcon || getCategoryIcon(category)} label={category} showLiveIndicator={result.group === 'live'} />
      <div className="moment-search-card-body">
        <h3>{result.title}</h3>
        {result.subtitle && <p>{result.subtitle}</p>}
        <div className="moment-search-card-meta">
          <span>{result.publisherName}</span>
          {result.publisherHandle && <span>@{result.publisherHandle.replace(/^@+/, '')}</span>}
        </div>
        {momentTypeLabels.length > 0 && (
          <span className="moment-search-session-types" aria-label="Moment types">
            <span className="moment-search-session-types-icon" aria-hidden="true">⚡</span>
            {visibleMomentTypeLabels.map((label) => (
              <span key={label} className="moment-search-session-type-chip">{label}</span>
            ))}
            {hiddenMomentTypeCount > 0 && (
              <span className="moment-search-session-type-chip" title={momentTypeLabels.join(', ')}>
                +{hiddenMomentTypeCount}
              </span>
            )}
          </span>
        )}
        <span className="moment-search-card-actions">
          <a
            className="moment-search-card-action"
            href={actionUrl}
            onClick={(event) => event.stopPropagation()}
          >
            {actionLabel}
          </a>
          {result.group === 'live' && (
            <a
              className="moment-search-card-action moment-search-card-action-live"
              href={getSessionPlayerUrl(result)}
              onClick={(event) => event.stopPropagation()}
            >
              Watch Live
            </a>
          )}
        </span>
        {time && <span className="moment-search-card-time">{time}</span>}
      </div>
    </article>
  );
};

const MomentCard: React.FC<{ result: MomentSearchMomentResult }> = ({ result }) => {
  const time = formatDateTime(result.occurredAtIso);
  const description = isDefaultMomentDescription(result.description) ? '' : result.description;
  const title = result.sessionTitle || result.title.replace(new RegExp(`^${escapeRegExp(result.momentTypeLabel)}\\s+in\\s+`, 'i'), '');
  const isRecentMoment = isWithinLastMinute(result.occurredAtIso);
  const playerUrl = getMomentPlayerUrl(result);

  return (
    <a className="moment-search-card moment-search-moment-card" href={playerUrl} aria-label={`Cut to the moment: ${result.title}`}>
      <SearchCardVisual icon={result.momentTypeIcon || '⚡'} label={result.momentTypeLabel} showLiveIndicator={isRecentMoment} />
      <div className="moment-search-card-body">
        <h3>{title}</h3>
        {description && <p>{description}</p>}
        <div className="moment-search-card-meta">
          <span>{result.publisherName}</span>
        </div>
        <span className="moment-search-card-actions">
          <span className="moment-search-card-action moment-search-card-action-cut">Cut to the moment</span>
        </span>
        {time && <span className="moment-search-card-time">{time}</span>}
      </div>
    </a>
  );
};

const TickerMomentCard: React.FC<{ result: MomentTickerResult }> = ({ result }) => {
  const relativeTime = formatRelativeTime(result.occurredAtIso);
  const absoluteTime = formatDateTime(result.occurredAtIso);
  const description = isDefaultMomentDescription(result.description) ? '' : result.description;
  const title = result.sessionTitle || result.title.replace(new RegExp(`^${escapeRegExp(result.momentTypeLabel)}\\s+in\\s+`, 'i'), '');
  const playerUrl = getMomentPlayerUrl(result);
  const isFresh = isWithinLastMinute(result.occurredAtIso);

  return (
    <a className={`moment-search-ticker-item${isFresh ? ' is-fresh' : ''}`} href={playerUrl} aria-label={`Watch live moment: ${result.title}`}>
      <div className="moment-search-ticker-icon" aria-hidden="true">
        <span className="moment-search-card-live-dot" />
        <span>{result.momentTypeIcon || '⚡'}</span>
      </div>
      <div className="moment-search-ticker-body">
        <div className="moment-search-ticker-topline">
          <span className="moment-search-ticker-live-pill">Live</span>
          <span>{result.momentTypeLabel}</span>
          {relativeTime && <time dateTime={result.occurredAtIso ?? undefined} title={absoluteTime}>{relativeTime}</time>}
        </div>
        <h3>{title}</h3>
        {description && <p>{description}</p>}
        <div className="moment-search-ticker-meta">
          <span>{result.publisherName}</span>
          {result.categoryLabel && <span>{result.categoryLabel}</span>}
        </div>
      </div>
      <span className="moment-search-ticker-action">Watch moment</span>
    </a>
  );
};

const PublisherCard: React.FC<{ result: MomentSearchPublisherResult }> = ({ result }) => (
  <a className="moment-search-card moment-search-publisher-card" href={result.fanUrl} aria-label={`View publisher: ${result.displayName}`}>
    <SearchCardVisual imageUrl={result.avatarUrl} icon={result.categoryIcon || result.displayName.slice(0, 1).toUpperCase()} label={result.categoryLabel || 'Publisher'} />
    <div className="moment-search-card-body">
      <h3>{result.displayName}</h3>
      <p>@{result.handle.replace(/^@+/, '')}</p>
      <span className="moment-search-card-actions">
        <span className="moment-search-card-action">View publisher</span>
      </span>
      <span className="moment-search-publisher-counts">
        <span>{formatCompactCount(result.publishedMomentCount ?? 0)} moments</span>
        <span>{formatCompactCount(result.followerCount ?? 0)} followers</span>
      </span>
    </div>
  </a>
);

const ResultSection: React.FC<{
  title: string;
  subtitle: string;
  children: React.ReactNode;
  empty: boolean;
  canLoadMore?: boolean;
  loadingMore?: boolean;
  loadMoreError?: string;
  onLoadMore?: () => void;
}> = ({ title, subtitle, children, empty, canLoadMore = false, loadingMore = false, loadMoreError = '', onLoadMore }) => {
  if (empty) {
    return null;
  }

  return (
    <section className="moment-search-result-section">
      <div className="moment-search-result-section-header">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <div className="moment-search-result-grid">{children}</div>
      {canLoadMore && onLoadMore && (
        <div className="moment-search-load-more-wrap">
          <button
            type="button"
            className="moment-search-load-more"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading more...' : 'Load more'}
          </button>
        </div>
      )}
      {loadMoreError && <p className="moment-search-load-more-error">{loadMoreError}</p>}
    </section>
  );
};

export const MomentSearchPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';
  const mode: SearchMode = searchParams.get('mode') === 'ticker' ? 'ticker' : 'search';
  const [queryDraft, setQueryDraft] = useState(urlQuery);
  const [response, setResponse] = useState<MomentSearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tickerResponse, setTickerResponse] = useState<MomentTickerResponse | null>(null);
  const [tickerLoading, setTickerLoading] = useState(true);
  const [tickerRefreshing, setTickerRefreshing] = useState(false);
  const [tickerError, setTickerError] = useState('');
  const [tickerPaused, setTickerPaused] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ResultFilter>('all');
  const [loadMoreState, setLoadMoreState] = useState<Record<LoadMoreGroup, LoadMoreState>>(createEmptyLoadMoreState);

  useEffect(() => {
    setQueryDraft(urlQuery);
    setActiveFilter('all');
    setLoadMoreState(createEmptyLoadMoreState());
    setTickerPaused(false);
  }, [mode, urlQuery]);

  useEffect(() => {
    if (mode !== 'search') {
      return;
    }

    let cancelled = false;
    const loadResults = async () => {
      setLoading(true);
      setError('');
      try {
        const nextResponse = await searchPublicMoments({ query: urlQuery, limit: SEARCH_PAGE_GROUP_LIMIT });
        if (!cancelled) {
          setResponse(nextResponse);
        }
      } catch (loadError) {
        console.error('[MomentSearchPage] Failed to load search results:', loadError);
        if (!cancelled) {
          setResponse(null);
          setError('Search is temporarily unavailable. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadResults();
    return () => {
      cancelled = true;
    };
  }, [mode, urlQuery]);

  useEffect(() => {
    if (mode !== 'ticker') {
      return;
    }

    let cancelled = false;
    let intervalId: number | undefined;
    const loadTicker = async (initial: boolean) => {
      if (initial) {
        setTickerLoading(true);
      } else {
        setTickerRefreshing(true);
      }
      setTickerError('');
      try {
        const nextResponse = await fetchLiveMomentTicker({ query: urlQuery, limit: LIVE_TICKER_LIMIT });
        if (!cancelled) {
          setTickerResponse(nextResponse);
        }
      } catch (loadError) {
        console.error('[MomentSearchPage] Failed to load live moment ticker:', loadError);
        if (!cancelled) {
          setTickerError('Live ticker is temporarily unavailable. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setTickerLoading(false);
          setTickerRefreshing(false);
        }
      }
    };

    loadTicker(true);

    if (!tickerPaused) {
      const pollIntervalMs = tickerResponse?.meta.pollIntervalMs ?? LIVE_TICKER_FALLBACK_POLL_INTERVAL_MS;
      intervalId = window.setInterval(() => {
        loadTicker(false);
      }, pollIntervalMs);
    }

    return () => {
      cancelled = true;
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
    };
  }, [mode, tickerPaused, tickerResponse?.meta.pollIntervalMs, urlQuery]);

  const totalResults = useMemo(() => countResults(response), [response]);
  const resultFilters = useMemo<ResultFilterOption[]>(() => {
    if (!response) {
      return [];
    }

    const filters: ResultFilterOption[] = [
      { id: 'all', label: 'All', count: totalResults },
      { id: 'liveMoments', label: 'Live moments', count: response.groups.liveMoments.length },
      { id: 'live', label: 'Live sessions', count: response.groups.live.length },
      { id: 'upcoming', label: 'Upcoming', count: response.groups.upcoming.length },
      { id: 'moments', label: 'Past moments', count: response.groups.moments.length },
      { id: 'past', label: 'Past sessions', count: response.groups.past.length },
      { id: 'publishers', label: 'Publishers', count: response.groups.publishers.length },
    ];

    return filters.filter((filter) => filter.id === 'all' || filter.count > 0);
  }, [response, totalResults]);

  const buildSearchPath = (nextQuery: string, nextMode: SearchMode): string => {
    const trimmed = nextQuery.trim();
    const params = new URLSearchParams();
    if (trimmed) {
      params.set('q', trimmed);
    }
    if (nextMode === 'ticker') {
      params.set('mode', 'ticker');
    }
    const queryString = params.toString();
    return queryString ? `/search?${queryString}` : '/search';
  };

  const submitSearch = (nextQuery: string, nextMode: SearchMode = mode) => {
    navigate(buildSearchPath(nextQuery, nextMode));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitSearch(queryDraft);
  };

  const clearSearch = () => {
    setQueryDraft('');
    submitSearch('', mode);
  };

  const switchMode = (nextMode: SearchMode) => {
    submitSearch(queryDraft, nextMode);
  };

  const loadMore = async (group: LoadMoreGroup) => {
    const pagination = getGroupPagination(response, group);
    if (!pagination.hasMore || !pagination.nextCursor || loadMoreState[group].loading) {
      return;
    }

    setLoadMoreState((current) => ({
      ...current,
      [group]: { loading: true, error: '' },
    }));

    try {
      const nextResponse = await searchPublicMoments({
        query: urlQuery,
        limit: SEARCH_PAGE_GROUP_LIMIT,
        group,
        cursor: pagination.nextCursor,
      });
      setResponse((current) => (current ? appendSearchGroup(current, nextResponse, group) : nextResponse));
    } catch (loadError) {
      console.error(`[MomentSearchPage] Failed to load more ${group} results:`, loadError);
      setLoadMoreState((current) => ({
        ...current,
        [group]: { loading: false, error: 'Could not load more results. Please try again.' },
      }));
      return;
    }

    setLoadMoreState((current) => ({
      ...current,
      [group]: { loading: false, error: '' },
    }));
  };

  const showShortQueryNotice = response?.meta.searched === false && response.normalizedQuery.length > 0;
  const showFilter = (filter: ResultFilter): boolean => activeFilter === 'all' || activeFilter === filter;

  return (
    <div className="moment-search-page">
      <header className="moment-search-header">
        <Link to="/" className="moment-search-logo">
          <img src="/logo-transparent-no-slogan.png" alt={intl.formatMessage({ id: 'landing.brand.alt' })} />
        </Link>
        <Link to="/" className="moment-search-back-link">
          {intl.formatMessage({ id: 'landing.contact.backToHome' })}
        </Link>
      </header>

      <main className="moment-search-main">
        <section className="moment-search-hero" aria-labelledby="moment-search-title">
          <h1 id="moment-search-title">
            <span className="gradient-text">{mode === 'ticker' ? 'Live Moment Ticker' : 'Find Moments'}</span>
          </h1>
          <p>
            {mode === 'ticker'
              ? 'Watch public live moments roll in as they happen. Filter by team, sport, creator, event, or moment type.'
              : 'Search live streams, upcoming events, public past moments, and publishers. Results are spoiler-safe and route into the HypeDirector fan experience when you want to watch or follow.'}
          </p>

          <form className="moment-search-form" onSubmit={handleSubmit}>
            <label htmlFor="moment-search-input">Search HypeDirector</label>
            <div className="moment-search-control">
              <div className="moment-search-input-wrap">
                <input
                  id="moment-search-input"
                  value={queryDraft}
                  onChange={(event) => setQueryDraft(event.target.value)}
                  placeholder="Search teams, creators, events, moments..."
                  maxLength={80}
                  autoFocus
                />
                {queryDraft && (
                  <button
                    className="moment-search-clear-button"
                    type="button"
                  onClick={clearSearch}
                    aria-label="Clear search"
                    title="Clear"
                  >
                    ×
                  </button>
                )}
              </div>
              <span className="moment-search-control-actions">
                <button
                  type="button"
                  className={mode === 'search' ? 'is-active' : ''}
                  onClick={() => switchMode('search')}
                  aria-pressed={mode === 'search'}
                >
                  Search
                </button>
                <button
                  type="button"
                  className={mode === 'ticker' ? 'is-active' : ''}
                  onClick={() => switchMode('ticker')}
                  aria-pressed={mode === 'ticker'}
                >
                  Ticker
                </button>
              </span>
            </div>
          </form>

          {mode === 'search' && resultFilters.length > 1 && (
            <div className="moment-search-filters" aria-label="Filter search results">
              {resultFilters.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  className={activeFilter === filter.id ? 'is-active' : ''}
                  onClick={() => setActiveFilter(filter.id)}
                  aria-pressed={activeFilter === filter.id}
                >
                  {filter.label}
                  <span>{filter.count}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="moment-search-summary" aria-live="polite">
          {mode === 'ticker' ? (
            tickerLoading ? (
              <span>Loading live ticker...</span>
            ) : tickerError ? (
              <span className="moment-search-error-text">{tickerError}</span>
            ) : tickerResponse?.meta.searched === false ? (
              <span>Type at least {tickerResponse?.meta.minimumQueryLength ?? 2} characters to filter the live ticker, or clear the field for all live moments.</span>
            ) : (
              <span>
                {tickerResponse?.items.length ?? 0} live moment{tickerResponse?.items.length === 1 ? '' : 's'}
                {tickerResponse?.query ? ` for "${tickerResponse.query}"` : ''}. Updates every 15 seconds.
              </span>
            )
          ) : loading ? (
            <span>Searching for moments...</span>
          ) : error ? (
            <span className="moment-search-error-text">{error}</span>
          ) : showShortQueryNotice ? (
            <span>Type at least {response?.meta.minimumQueryLength ?? 2} characters to search, or clear the field for curated results.</span>
          ) : response?.meta.defaultResults ? (
            <span>Showing curated discovery results.</span>
          ) : (
            <span>{totalResults} result{totalResults === 1 ? '' : 's'} for "{response?.query || urlQuery}"</span>
          )}
        </section>

        {mode === 'ticker' ? (
          tickerLoading ? (
            <div className="moment-search-loading">
              <div className="moment-search-spinner" />
              <span>Loading live ticker...</span>
            </div>
          ) : tickerResponse?.meta.searched === false && !tickerError ? (
            <div className="moment-search-empty">
              <h2>Keep typing</h2>
              <p>Use at least {tickerResponse.meta.minimumQueryLength} characters to filter the live ticker, or clear the search field to see all public live moments.</p>
            </div>
          ) : tickerResponse && tickerResponse.items.length > 0 ? (
            <section className="moment-search-ticker" aria-label="Live moment ticker">
              <div className="moment-search-ticker-header">
                <div>
                  <h2>Latest Live Moments</h2>
                  <p>Newest public moments from live sessions appear first.</p>
                </div>
                <div className="moment-search-ticker-controls">
                  {tickerRefreshing && <span>Refreshing...</span>}
                  <button type="button" onClick={() => setTickerPaused((current) => !current)}>
                    {tickerPaused ? 'Resume' : 'Pause'}
                  </button>
                </div>
              </div>
              <div className="moment-search-ticker-list">
                {tickerResponse.items.map((result) => <TickerMomentCard key={result.id} result={result} />)}
              </div>
            </section>
          ) : !tickerError ? (
            <div className="moment-search-empty">
              <h2>No live moments right now</h2>
              <p>Try a broader search or check back when public sessions are live.</p>
            </div>
          ) : null
        ) : loading ? (
          <div className="moment-search-loading">
            <div className="moment-search-spinner" />
            <span>Searching for moments...</span>
          </div>
        ) : totalResults === 0 && !error ? (
          <div className="moment-search-empty">
            <h2>No public moments found</h2>
            <p>Try another team, sport, creator, publisher, or clear the search to see curated results.</p>
          </div>
        ) : response ? (
          getResultFilterCount(response, activeFilter) === 0 ? (
            <div className="moment-search-empty">
              <h2>No results in this section</h2>
              <p>Choose another result type or switch back to All.</p>
            </div>
          ) : (
            <div className="moment-search-results">
              {showFilter('liveMoments') && (
                <ResultSection
                  title="Live moments"
                  subtitle="Newest public moments from sessions live right now."
                  empty={response.groups.liveMoments.length === 0}
                  canLoadMore={getGroupPagination(response, 'liveMoments').hasMore}
                  loadingMore={loadMoreState.liveMoments.loading}
                  loadMoreError={loadMoreState.liveMoments.error}
                  onLoadMore={() => loadMore('liveMoments')}
                >
                  {response.groups.liveMoments.map((result) => <TickerMomentCard key={result.id} result={result} />)}
                </ResultSection>
              )}

              {showFilter('live') && (
                <ResultSection
                  title="Live sessions"
                  subtitle="Public live sessions available right now."
                  empty={response.groups.live.length === 0}
                  canLoadMore={getGroupPagination(response, 'live').hasMore}
                  loadingMore={loadMoreState.live.loading}
                  loadMoreError={loadMoreState.live.error}
                  onLoadMore={() => loadMore('live')}
                >
                  {sortPeopleSessionsLast(response.groups.live).map((result) => <SessionCard key={result.id} result={result} />)}
                </ResultSection>
              )}

              {showFilter('upcoming') && (
                <ResultSection
                  title="Upcoming"
                  subtitle="Scheduled streams you can follow in the app."
                  empty={response.groups.upcoming.length === 0}
                  canLoadMore={getGroupPagination(response, 'upcoming').hasMore}
                  loadingMore={loadMoreState.upcoming.loading}
                  loadMoreError={loadMoreState.upcoming.error}
                  onLoadMore={() => loadMore('upcoming')}
                >
                  {sortPeopleSessionsLast(response.groups.upcoming).map((result) => <SessionCard key={result.id} result={result} />)}
                </ResultSection>
              )}

              {showFilter('moments') && (
                <ResultSection
                  title="Past moments"
                  subtitle="Individual public moments from ended sessions."
                  empty={response.groups.moments.length === 0}
                  canLoadMore={getGroupPagination(response, 'moments').hasMore}
                  loadingMore={loadMoreState.moments.loading}
                  loadMoreError={loadMoreState.moments.error}
                  onLoadMore={() => loadMore('moments')}
                >
                  {response.groups.moments.map((result) => <MomentCard key={result.id} result={result} />)}
                </ResultSection>
              )}

              {showFilter('past') && (
                <ResultSection
                  title="Past sessions"
                  subtitle="Ended sessions with fan-visible moments."
                  empty={response.groups.past.length === 0}
                  canLoadMore={getGroupPagination(response, 'past').hasMore}
                  loadingMore={loadMoreState.past.loading}
                  loadMoreError={loadMoreState.past.error}
                  onLoadMore={() => loadMore('past')}
                >
                  {sortPeopleSessionsLast(response.groups.past).map((result) => <SessionCard key={result.id} result={result} />)}
                </ResultSection>
              )}

              {showFilter('publishers') && (
                <ResultSection
                  title="Publishers"
                  subtitle="Public publishers and creators on HypeDirector."
                  empty={response.groups.publishers.length === 0}
                  canLoadMore={getGroupPagination(response, 'publishers').hasMore}
                  loadingMore={loadMoreState.publishers.loading}
                  loadMoreError={loadMoreState.publishers.error}
                  onLoadMore={() => loadMore('publishers')}
                >
                  {response.groups.publishers.map((result) => <PublisherCard key={result.id} result={result} />)}
                </ResultSection>
              )}
            </div>
          )
        ) : null}
      </main>
    </div>
  );
};
